import {
  createBaseThemeOptions,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  createUnifiedTheme,
} from '@backstage/theme';
import '../assests/styles.css';
import ScaniaSansBold from '../assests/fonts/ScaniaSans-Bold.woff2';
import ScaniaSansRegular from '../assests/fonts/ScaniaSans-Regular.woff2';
import ScaniaSansHeadlineBold from '../assests/fonts/ScaniaSansHeadline-Bold.woff2';

// Define custom font faces//
const styles = `
  @font-face {
    font-family: 'Scania Sans';
    font-style: normal;
    font-weight: 700;
    src: url(${ScaniaSansBold}) format('woff2');
  }
  @font-face {
    font-family: 'Scania Sans';
    font-style: normal;
    font-weight: 400;
    src: url(${ScaniaSansRegular}) format('woff2');
  }
  @font-face {
    font-family: 'Scania Sans Headline';
    font-style: normal;
    font-weight: 700;
    src: url(${ScaniaSansHeadlineBold}) format('woff2');
  }
`;

// Override page theme font colors
const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).forEach(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: '#041E42', // Custom font color override
  };
});

const backstageErrorPageOverrides = {
  BackstageErrorPage: {
    styleOverrides: {
      container: {
        alignContent: 'flex-start',
        backgroundColor: '#F5F6F7',
        color: '#0D0F13',
        minHeight: 'calc(100vh - 120px)',
        padding: '64px 40px',
        '@media (max-width: 999px)': {
          padding: '48px 24px 64px',
        },
        '& .MuiGrid-item': {
          flexBasis: '760px',
          maxWidth: '760px',
        },
        '& a': {
          color: '#041E42',
          fontWeight: 700,
          textDecoration: 'none',
        },
        '& a:hover, & a:focus-visible': {
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
        },
      },
      subtitle: {
        color: '#565B62',
        fontFamily: "'Scania Sans', Arial, sans-serif",
        fontSize: '1rem',
        lineHeight: 1.5,
        marginBottom: '8px',
      },
      title: {
        color: '#0D0F13',
        fontFamily: "'Scania Sans', Arial, sans-serif",
        letterSpacing: 0,
        paddingBottom: '24px',
        '&.MuiTypography-h2': {
          fontSize: '2rem',
          fontWeight: 700,
          lineHeight: 1.2,
          margin: '14px 0 0',
          paddingBottom: '24px',
        },
        '&.MuiTypography-h6': {
          color: '#172B48',
          fontSize: '1rem',
          lineHeight: 1.5,
          paddingBottom: 0,
        },
      },
    },
  },
  BackstageErrorPageMicDrop: {
    styleOverrides: {
      micDrop: {
        display: 'none',
      },
    },
  },
  BackstageErrorPageStackDetails: {
    styleOverrides: {
      title: {
        paddingBottom: '16px',
      },
    },
  },
};

// Create the theme
const apertureTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      background: {
        default: '#FFFFFF',
        paper: '#FAFAFA',
      },
      primary: {
        main: '#041E42',
        // light: '#FAFAFA',
      },
      secondary: {
        main: '#C8C9C7',
        light: '#97999B',
        dark: '#53565A',
      },
      grey: {
        50: '#F9FAFB', // Matching image palette
        100: '#EDEFF3', // Matching image palette
        200: '#E7E9EE', // Matching image palette
        300: '#DBDFE6', // Matching image palette
        400: '#CDD1DB', // Matching image palette
        500: '#B0B7C4', // Matching image palette
      },
      error: {
        main: '#D6001C',
        light: '#FF8F73',
        dark: '#DE350B',
      },
      warning: {
        main: '#FFAB00',
        light: '#FFE380',
        dark: '#FF8B00',
      },
      success: {
        main: '#36B37E',
        light: '#79F2C0',
        dark: '#006644',
      },
      info: {
        main: '#0065FF',
        light: '#4C9AFF',
        dark: '#0747A6',
      },
      navigation: {
        ...palettes.light.navigation,
        background: '#0D0F13', // Set sidebar background color to match dark theme
        color: '#FFFFFF',
        indicator: '#2684FF',
        navItem: {
          hoverBackground: 'rgba(116,118,121,0.6)',
        },
      },
      text: {
        primary: '#172B48',
      },
      // background: {
      //   default: '#FFFFFF',
      // },
    },
  }),
  typography: {
    htmlFontSize: 16,
    fontFamily: "'Scania Sans', Arial, sans-serif",
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      marginBottom: 10,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: 8,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 700,
      marginBottom: 6,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 700,
      marginBottom: 6,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 700,
      marginBottom: 4,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 700,
      marginBottom: 2,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
  },
  components: {
    ...backstageErrorPageOverrides,
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          backgroundColor: '#041E42',
          backgroundImage: 'unset', // Remove any default background image
        }),
        title: {
          color: '#FFFFFF',
          fontFamily: "'Scania Sans', Arial, sans-serif",
          fontSize: '2rem',
          lineHeight: 1.25,
        }, // Set title color to white
        subtitle: {
          color: '#FFFFFF',
          fontFamily: "'Scania Sans', Arial, sans-serif",
          fontSize: '1rem',
          lineHeight: 1.25,
        }, // Set subtitle color to white
        type: {
          color: '#FFFFFF',
          fontFamily: "'Scania Sans', Arial, sans-serif",
          fontSize: '1rem',
          lineHeight: 1.25,
        }, // Set type color to white
      },
    },
  },
});

// Append custom font styles to the head
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default apertureTheme;
